import React, { Component } from 'react'
import { Form, HelpBlock } from 'react-bootstrap'
import '../App.css'
import { embedComponent } from '../embed'
import Field from '../components/Field'
import CurrencyField from '../components/CurrencyField'
import formatCurrency from '../utils/formatters/formatCurrency'
import parseIntValue from '../utils/parsers/parseIntValue'
import Calculator from '../components/Calculator/index'
import PercentageField from '../components/PercentageField/index'
import CalculatorResults from '../components/CalculatorResults/index'
import CalculatorResult from '../components/CalculatorResult/index'
import CalculatorButtons from '../components/CalculatorButtons/index'
import { minMaxValidator } from '../utils/validators/index'
import formatPercentage from '../utils/formatters/formatPercentage'
import Annotations from '../components/Annotations/index'

const popoverMessages = {
  estimatedDailyCost: {
    title: 'Estimated daily cost',
    body: [
      'Estimated cost per day for care. In 2016, the US average for a semi private nursing home is $225/day. For assisted living it is $119.',
      '<a href="/#/articles/82" target="_blank">Click here to learn more about long term care costs</a>.'
    ],
  },
  yearsOfCare: {
    title: 'Years of care',
    body: [
      'This is the number of years care will be required.',
      '<a href="/#/articles/78" target="_blank">Click here to learn more about long term care costs</a>.',
    ],
  },
  inflationRate: {
    title: 'Inflation rate',
    body: [
      'The inflation rate plays an important role in future costs.',
      'For 2018-2022 the US inflation rate is projected to be between 2% and 3%.',
    ],
  },
  totalCosts: {
    title: 'Total costs',
    body: [
      'This is the inflation- adjusted total projected cost.',
      'This is the total amount that must be covered.',
    ],
  },
}

class LongTermCareCostsCalculator extends Component {
  constructor (props) {
    super(props)

    this.state = LongTermCareCostsCalculator.getInitialState()

    this.clearState = this.clearState.bind(this)
    this.onValidation = this.onValidation.bind(this)
  }

  static getInitialState() {
    return {
      estimatedDailyCost: 119,
      yearsOfCare: null,
      inflationRate: 2.5,
      fieldErrors: {},
    }
  }

  handleChange (value, fieldName) {
    this.setState({ [fieldName]: value })
  }

  clearState () {
    this.setState(LongTermCareCostsCalculator.getInitialState())
  }

  onValidation(name, error) {
    if (typeof error !== 'undefined') {
      // Has errors
      if (!this.state.fieldErrors[name]) {
        this.setState({ fieldErrors: Object.assign({}, this.state.fieldErrors, { [name]: error }) })
      }
    } else {
      // No errors
      if (this.state.fieldErrors[name]) {
        const newErrors = Object.assign({}, this.state.fieldErrors)
        delete newErrors[name]
        this.setState({ fieldErrors: newErrors })
      }
    }
  }

  /**
   * Formula for each future year:
   * FYn = TC(1 + r)^(n-1)
   *
   * FYn = Cost in future year n
   * TC = Today's cost
   * r = interest rate
   * n = year
   *
   * Perform calculation for as many years as indicated in "Years of care" field.
   * Add the cost of each year to get total.
   */
  calculateTotalCosts () {
    const { estimatedDailyCost, inflationRate, yearsOfCare } = this.state

    if (!estimatedDailyCost || !inflationRate || !yearsOfCare) {
      return undefined
    }

    const currentCost = estimatedDailyCost * 365
    const interest = 1 + (inflationRate / 100)

    // Start with current cost, then add cost for future years.
    let totalCost = currentCost

    for (let i = 1; i < yearsOfCare; i++) {
      totalCost += currentCost * Math.pow(interest, i)
    }

    return totalCost
  }

  render () {
    const {
      estimatedDailyCost,
      yearsOfCare,
      inflationRate,
    } = this.state

    const calculatedTotalCosts = this.calculateTotalCosts();
    const hasErrors = Object.keys(this.state.fieldErrors).length > 0

    return (
      <Calculator title="Long Term Care Costs Calculator">
        <div className="calculator-body">
          <Form>
            <CurrencyField
              name="estimatedDailyCost"
              label="Estimated Daily Cost"
              annotationLabel="*"
              value={estimatedDailyCost}
              onChange={(e, value) => this.handleChange(value, 'estimatedDailyCost') }
              popover={popoverMessages.estimatedDailyCost}
              validator={minMaxValidator(1, 9999, formatCurrency)}
              onValidation={this.onValidation}
            />

            <Field
              type="number"
              name="yearsOfCare"
              label="Years of Care"
              value={yearsOfCare}
              parse={parseIntValue}
              onChange={(e, value) => this.handleChange(value, 'yearsOfCare') }
              popover={popoverMessages.yearsOfCare}
              validator={minMaxValidator(1, 99)}
              onValidation={this.onValidation}
            />

            <PercentageField
              name="inflationRate"
              label="Inflation Rate"
              value={inflationRate}
              onChange={(e, value) => this.handleChange(value, 'inflationRate') }
              popover={popoverMessages.inflationRate}
              validator={minMaxValidator(0, 99, formatPercentage)}
              onValidation={this.onValidation}
            />
          </Form>
          <CalculatorResults>
            <CalculatorResult
              label={'Total Costs'}
              value={formatCurrency(Math.ceil((!hasErrors && calculatedTotalCosts) || 0))}
              popover={popoverMessages.totalCosts}
            />
          </CalculatorResults>
          <CalculatorButtons onClear={this.clearState} />
          <Annotations>
            <HelpBlock><sup>*</sup> 2016 US average for semi private nursing home is $225/day. For assisted living it is $119.</HelpBlock>
          </Annotations>
        </div>
      </Calculator>
    )
  }
}

embedComponent(LongTermCareCostsCalculator, 'tgm-long-term-care-costs-calc', 'tgm-long-term-care-costs-calc-embed')

export default LongTermCareCostsCalculator



// WEBPACK FOOTER //
// ./src/calculators/LongTermCareCostsCalculator.js